import { registerComponent } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "video-player",
  class VideoPlayer extends WYATTElement {
    constructor() {
      super();

      this.nativeVideo = this.querySelector("video");
      this.externalVideo = this.querySelector("iframe");
      this.startPlayButton;
      this.playButton;
      this.pauseButton;

      this.addStartPlayButton();
      this.addControls();
      this.addPreviews();
      this.bindEvents();
      this.autoplayExternalVideo();
    }

    // External embeds have no <video> element to autoplay, so render the
    // iframe immediately. Muting is required for browsers to allow it.
    autoplayExternalVideo() {
      if (!this.config.autoplay || this.nativeVideo) {
        return;
      }

      if (this.config.host === "youtube" || this.config.host === "vimeo") {
        this.renderVideo();
      }
    }

    addStartPlayButton() {
      if (this.config.autoplay || this.config.nativeControls) {
        return;
      }

      const template = this.querySelector("[data-start-play]").content;
      const playButton = document.importNode(template, true);

      this.startPlayButton = playButton.querySelector(
        "[data-start-play-button]"
      );

      this.appendChild(playButton);
    }

    addControls() {
      if (this.config.host === "youtube" || this.config.host === "vimeo") {
        return;
      }

      if (this.config.nativeControls) {
        return;
      }

      const template = this.querySelector("[data-controls]").content;
      const controls = document.importNode(template, true);

      this.playButton = controls.querySelector('[data-control="play"]');
      this.pauseButton = controls.querySelector('[data-control="pause"]');

      this.appendChild(controls);
    }

    addPreviews() {
      if (this.config.autoplay) {
        return;
      }

      if (this.config.previewImage) {
        this.startPlayButton.insertAdjacentHTML(
          "beforeend",
          this.config.previewImage
        );

        return;
      }

      this.renderExternalPreview();
    }

    bindEvents() {
      if (this.config.previewOnly) {
        return;
      }

      this.startPlayButton?.addEventListener(
        "click",
        this.handleStartPlay.bind(this)
      );

      if (!this.config.loop) {
        this.nativeVideo?.addEventListener("ended", this.reset.bind(this));
      }

      this.playButton?.addEventListener("click", this.play.bind(this));
      this.pauseButton?.addEventListener("click", this.pause.bind(this));

      this.handleModalContext();
    }

    handleStartPlay() {
      if (this.nativeVideo) {
        this.play();
      } else {
        this.renderVideo();
      }
    }

    async renderExternalPreview() {
      if (this.config.host === "youtube") {
        this.previewImageSrc = `https://i.ytimg.com/vi/${this.config.externalID}/maxresdefault.jpg`;
      } else {
        this.previewImageSrc = await this.fetchVimeoPreviewImageSrc();
      }

      this.previewImage = document.createElement("img");
      this.previewImage.setAttribute("src", this.previewImageSrc);
      this.previewImage.setAttribute(
        "class",
        "video-player__preview-image fit-image"
      );
      this.previewImage.setAttribute("loading", "lazy");

      if (this.config.host === "youtube") {
        // Not every video has a maxresdefault thumbnail — YouTube answers
        // with a 120x90 placeholder instead. Fall back to hqdefault, which
        // exists for every video.
        this.previewImage.addEventListener("load", () => {
          if (
            this.previewImage.naturalWidth <= 120 &&
            !this.previewImage.src.includes("hqdefault")
          ) {
            this.previewImage.setAttribute(
              "src",
              `https://i.ytimg.com/vi/${this.config.externalID}/hqdefault.jpg`
            );
          }
        });
      }

      this.startPlayButton.append(this.previewImage);
    }

    async fetchVimeoPreviewImageSrc() {
      const response = await fetch(
        `https://vimeo.com/api/oembed.json?url=https%3A//vimeo.com/${this.config.externalID}&format=json&width=1200`
      );
      const data = await response.json();

      return data.thumbnail_url;
    }

    renderVideo() {
      if (this.config.host === "youtube") {
        this.videoSrc = `https://www.youtube.com/embed/${this.config.externalID}`;
      } else {
        this.videoSrc = `https://player.vimeo.com/video/${this.config.externalID}`;
      }

      const params = new URLSearchParams({ autoplay: "1" });

      if (this.config.autoplay) {
        // Muted inline playback is required for autoplay; hide the chrome
        // and loop so the embed behaves like a background video.
        params.set("mute", "1");
        params.set("muted", "1");
        params.set("playsinline", "1");
        params.set("controls", "0");
        params.set("disablekb", "1");
        params.set("rel", "0");
        params.set("iv_load_policy", "3");

        if (this.config.host === "youtube") {
          // Required for the postMessage commands in
          // sendBackgroundPlayerCommands (captions off, play, mute).
          params.set("enablejsapi", "1");
          params.set("origin", window.location.origin);
        }

        if (this.config.host === "vimeo") {
          params.set("background", "1");
        }
      }

      if (this.config.loop) {
        params.set("loop", "1");

        if (this.config.host === "youtube") {
          params.set("playlist", this.config.externalID);
        }
      }

      this.externalVideo =
        this.externalVideo || document.createElement("iframe");
      const sizeSource = this.startPlayButton || this;
      const width = sizeSource.offsetWidth;
      const height = sizeSource.offsetHeight;

      this.externalVideo.setAttribute("src", `${this.videoSrc}?${params}`);
      this.externalVideo.setAttribute("frameborder", "0");
      this.externalVideo.setAttribute("allowfullscreen", "1");
      this.externalVideo.setAttribute("allow", "autoplay; encrypted-media");

      if (this.config.autoplay) {
        // Decorative background video: keep it out of the tab order and
        // the accessibility tree.
        this.externalVideo.setAttribute("aria-hidden", "true");
        this.externalVideo.setAttribute("tabindex", "-1");
        this.externalVideo.setAttribute("title", "");
      }
      this.externalVideo.setAttribute("class", "video-player__video fit-image");
      this.externalVideo.setAttribute("width", width);
      this.externalVideo.setAttribute("height", height);
      this.externalVideo.setAttribute("frameborder", "0");

      this.setAttribute("ready", true);
      this.appendChild(this.externalVideo);

      if (this.config.autoplay && this.config.host === "youtube") {
        this.externalVideo.addEventListener(
          "load",
          this.sendBackgroundPlayerCommands.bind(this)
        );
      }

      this.play();
    }

    /*
      YouTube force-enables captions on muted autoplay embeds, and
      cc_load_policy can only force them ON. Disabling requires the
      IFrame API message protocol: a `listening` handshake, then
      unloadModule + setOption("captions", "track", {}) commands. The
      player becomes ready an unpredictable time after the iframe's load
      event, so commands are re-sent on an interval and whenever the
      player itself sends a message, within a bounded window.
    */
    sendBackgroundPlayerCommands() {
      const origin = "https://www.youtube.com";

      const send = () => {
        const messages = [
          { event: "listening", id: this.config.id, channel: "widget" },
          { event: "command", func: "unloadModule", args: ["captions"] },
          { event: "command", func: "unloadModule", args: ["cc"] },
          { event: "command", func: "setOption", args: ["captions", "track", {}] },
          { event: "command", func: "setOption", args: ["cc", "track", {}] },
          { event: "command", func: "mute", args: [] },
          { event: "command", func: "playVideo", args: [] },
        ];

        messages.forEach((message) => {
          this.externalVideo.contentWindow?.postMessage(
            JSON.stringify({ channel: "widget", ...message }),
            origin
          );
        });

        this.backgroundCommandCount = (this.backgroundCommandCount || 0) + 1;

        if (this.backgroundCommandCount >= 30) {
          this.stopBackgroundPlayerCommands();
        }
      };

      this.stopBackgroundPlayerCommands();
      this.backgroundCommandCount = 0;
      this.backgroundCommandInterval = setInterval(send, 800);

      // React to the player coming alive (its first postMessages back)
      // rather than relying on timing alone.
      this.backgroundMessageHandler = (event) => {
        if (
          event.origin === origin &&
          event.source === this.externalVideo?.contentWindow
        ) {
          send();
          window.removeEventListener("message", this.backgroundMessageHandler);
        }
      };
      window.addEventListener("message", this.backgroundMessageHandler);

      send();
    }

    stopBackgroundPlayerCommands() {
      clearInterval(this.backgroundCommandInterval);

      if (this.backgroundMessageHandler) {
        window.removeEventListener("message", this.backgroundMessageHandler);
      }
    }

    handleModalContext() {
      const modal = this.closest("[data-dialog]");

      if (modal) {
        modal.addEventListener("modalOpen", this.renderVideo.bind(this));
        modal.addEventListener("modalClose", this.reset.bind(this));
      }
    }

    play() {
      this.nativeVideo?.play();
      this.setAttribute("playback-state", "playing");
    }

    pause() {
      this.nativeVideo?.pause();
      this.setAttribute("playback-state", "paused");
    }

    reset() {
      this.externalVideo?.remove();
      this.setAttribute("playback-state", "initial");
    }
  }
);
